/**
 * Pure helpers for automated dunning (tracker item 36): the Invoices >
 * Dunning page, Settings > Dunning, the invoice hold panel and the AR aging
 * badge. Amounts come from the server as decimal strings; the only client
 * arithmetic is the total of the selected customers, done in integer cents.
 */

import * as types from '../api/types';
import { getErrorCode, getErrorMessage } from '../utils/apiError';
import { centsToDecimal, toCents } from './financeChargeUi';
import { invoiceDate, invoiceMoney } from './invoiceUi';
import { notificationTemplatePath } from './settings/notificationUi';

export const DUNNING_PATH = '/invoices/dunning';
export const DUNNING_SETTINGS_PATH = '/settings/dunning';
export const MAX_DUNNING_STEPS = 6;
export const MIN_STEP_DAYS = 1;
export const MAX_STEP_DAYS = 365;

/** Link to a step's template page in Settings > Notifications. */
export function templatePath(eventKey: string): string {
  return notificationTemplatePath(eventKey);
}

/** Every customer the preview would remind is selected by default. */
export function defaultCustomerSelection(rows: types.DunningPreviewRow[]): string[] {
  return rows.map((row) => row.customerId);
}

/** Select-all checkbox: every customer, or nothing when all are selected. */
export function toggleAllCustomers(rows: types.DunningPreviewRow[], selected: string[]): string[] {
  const ids = rows.map((row) => row.customerId);
  return ids.length > 0 && ids.every((id) => selected.includes(id)) ? [] : ids;
}

/** Total overdue of the selected customers. */
export function selectedOverdueTotal(rows: types.DunningPreviewRow[], selected: string[]): string {
  return centsToDecimal(rows.filter((row) => selected.includes(row.customerId)).reduce((sum, row) => sum + toCents(row.totalOverdue), 0));
}

export function runConfirmMessage(count: number, total: string): string {
  const customers = `${count} ${count === 1 ? 'customer' : 'customers'}`;
  return `Queue overdue reminders to ${customers} (${invoiceMoney(total)} overdue)? Each customer gets one email listing their overdue invoices. `
    + 'Customers who cannot be emailed are recorded as skipped, with the reason.';
}

export const MODE_LABELS: Record<string, string> = { OFF: 'Off', ON: 'On' };

export const TRIGGER_LABELS: Record<string, string> = { MANUAL: 'Manual', NIGHTLY: 'Daily' };

/** What the tenant's mode means for the Dunning page. */
export function dunningModeDescription(mode: string, hour: number): string {
  const time = `${String(hour).padStart(2, '0')}:00 UTC`;
  return mode === 'ON'
    ? `Reminders are queued automatically once a day, from ${time}. Use this page to check who is next or to send them now.`
    : 'Automatic reminders are off. You can still send the reminders below by hand from this page. An administrator can turn them on under Settings > Dunning.';
}

const REASON_LABELS: Record<string, string> = {
  CUSTOMER_EXEMPT: 'Customer excluded from reminders',
  ON_HOLD: 'On hold',
  BEFORE_START_DATE: 'Due before the start date',
  BELOW_STEP: 'Late fee not yet overdue long enough',
  LATE_FEES_ONLY: 'Only late fees overdue',
  NO_STEP_REACHED: 'Not overdue long enough yet',
  ALREADY_REMINDED: 'Already reminded at this step',
  NO_EMAIL: 'No email address',
  EMAIL_INVALID: 'Email bounced',
  RECENTLY_REMINDED: 'Reminded recently',
  NO_STEPS: 'No active step in the schedule',
  NOTHING_OVERDUE: 'Nothing overdue',
  // Notification outcomes (the email is recorded skipped, or nothing is recorded for EVENT_DISABLED)
  NOTIFICATIONS_DISABLED: 'Notifications are off',
  EVENT_DISABLED: 'Reminder template is off',
  NOT_CONFIGURED: 'No verified sending connection',
  INVALID_ADDRESS: 'Invalid email address',
  OPTED_OUT: 'Customer opted out',
  INVALID_SUBJECT: 'Invalid subject',
  ERROR: 'Error',
};

export function dunningReasonLabel(reason: string | null | undefined): string {
  if (!reason) return '';
  return REASON_LABELS[reason] ?? reason;
}

/** "Step 2 (21 days)" */
export function stepLabel(step: { position: number; daysOverdue: number } | null | undefined): string {
  if (!step) return '';
  return `Step ${step.position} (${step.daysOverdue} days)`;
}

/** Badge text on AR aging / invoice rows: "Reminded (step 2)". */
export function remindedLabel(step: number | null | undefined): string | null {
  return step ? `Reminded (step ${step})` : null;
}

/** "Not before 10/21/2026" for a customer reminded too recently. */
export function rowNote(row: types.DunningPreviewRow): string | null {
  if (row.reason === 'RECENTLY_REMINDED' && row.nextReminderDate) return `Next reminder from ${invoiceDate(row.nextReminderDate)}`;
  return row.message;
}

export const NOTICE_STATUS_LABELS: Record<string, string> = {
  QUEUED: 'Sent',
  SKIPPED: 'Not sent',
  SUPERSEDED: 'Skipped (a later step was reached)',
};

/** A user-facing message for a preview / run failure. */
export function dunningError(err: unknown, fallback: string): string {
  switch (getErrorCode(err)) {
    case 'CUSTOMER_NOT_FOUND':
      return 'One of the selected customers no longer exists. Nothing was sent. Refresh the preview and try again.';
    case 'CONFLICT':
      return 'The schedule was changed by someone else at the same time. Reload the page and try again.';
    default:
      return getErrorMessage(err, fallback);
  }
}

// ============================================================ schedule editor

/** A row of the schedule editor (days kept as text while typing). */
export interface StepDraft {
  /** Existing slot (keeps the step's history); undefined for a new step */
  eventKey?: string;
  days: string;
  isActive: boolean;
  /** For display: the template label of an existing step */
  templateLabel?: string;
  templateActive?: boolean;
}

export function toStepDrafts(steps: types.DunningStep[]): StepDraft[] {
  return [...steps]
    .sort((a, b) => a.daysOverdue - b.daysOverdue)
    .map((s) => ({ eventKey: s.eventKey, days: String(s.daysOverdue), isActive: s.isActive, templateLabel: s.templateLabel, templateActive: s.templateActive }));
}

/** A new step a few days after the last one (capped at 365). */
export function newStepDraft(drafts: StepDraft[]): StepDraft {
  const last = drafts.reduce((max, d) => Math.max(max, Number(d.days) || 0), 0);
  return { days: String(Math.min(MAX_STEP_DAYS, last > 0 ? last + 7 : 7)), isActive: true };
}

/** Per-row problems ("" = fine): a whole number 1-365, no two steps on the same day. */
export function validateStepDrafts(drafts: StepDraft[]): string[] {
  const counts = new Map<number, number>();
  for (const d of drafts) {
    const n = Number(d.days);
    if (/^\d+$/.test(d.days.trim())) counts.set(n, (counts.get(n) ?? 0) + 1);
  }
  return drafts.map((d) => {
    const text = d.days.trim();
    if (!/^\d+$/.test(text)) return 'Enter a whole number of days';
    const n = Number(text);
    if (n < MIN_STEP_DAYS || n > MAX_STEP_DAYS) return `Between ${MIN_STEP_DAYS} and ${MAX_STEP_DAYS} days`;
    if ((counts.get(n) ?? 0) > 1) return 'Two steps cannot use the same number of days';
    return '';
  });
}

/** The PUT body: steps sorted by days (the order is always by days). */
export function toReplaceStepsRequest(drafts: StepDraft[]): types.ReplaceDunningStepsRequest {
  return {
    steps: [...drafts]
      .map((d) => ({ ...(d.eventKey ? { eventKey: d.eventKey } : {}), daysOverdue: Number(d.days.trim()), isActive: d.isActive }))
      .sort((a, b) => a.daysOverdue - b.daysOverdue),
  };
}

/** Problems with the notification setup that stop reminders from going out. */
export function sendingWarning(settings: Pick<types.NotificationSettings, 'enabled' | 'providerStatus'> | null): string | null {
  if (!settings) return null;
  if (settings.providerStatus !== 'VERIFIED') {
    return 'No verified email connection: reminders would be recorded as not sent. Set up and verify the connection in Settings > Notifications.';
  }
  if (!settings.enabled) {
    return 'Notifications are turned off: reminders would be recorded as not sent. Turn them on in Settings > Notifications.';
  }
  return null;
}
